import { useState, useEffect } from "react";

// Hook to manage table data filtering
export const useTableDataFilter = (tableData, filteredTableData, setFilteredTableData) => {
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [filterConditions, setFilterConditions] = useState([]);
    
    // Extract column names from the first row for filter setup
    useEffect(() => {
        if (tableData && tableData.length > 0) {
            setAvailableColumns(Object.keys(tableData[0]));
        }
    }, [tableData]);
    
    // Open filter modal
    const openFilterModal = () => {
        setFilterModalOpen(true);
    };
    
    // Close filter modal
    const closeFilterModal = () => {
        setFilterModalOpen(false);
    };
    
    // Add a new filter condition
    const addFilterCondition = () => {
        if (availableColumns.length > 0) {
            setFilterConditions([...filterConditions, {
                column: availableColumns[0],
                operator: "contains",
                value: ""
            }]);
        }
    };
    
    // Remove a filter condition
    const removeFilterCondition = (index) => {
        const newConditions = [...filterConditions];
        newConditions.splice(index, 1);
        setFilterConditions(newConditions);
    };
    
    // Update a specific filter condition
    const updateFilterCondition = (index, field, value) => {
        const newConditions = [...filterConditions];
        newConditions[index] = { ...newConditions[index], [field]: value };
        setFilterConditions(newConditions);
    };
    
    // Apply all filters to the table data
    const applyFilters = () => {
        if (filterConditions.length === 0) {
            // If no conditions, show all data
            setFilteredTableData(tableData);
        } else {
            const filtered = tableData.filter(row => {
                // A row matches if ALL conditions match (AND logic)
                return filterConditions.every(condition => {
                    const { column, operator, value } = condition;
                    const cellValue = String(row[column] || "").toLowerCase();
                    const filterValue = String(value).toLowerCase();
                    
                    switch (operator) {
                        case "contains":
                            return cellValue.includes(filterValue);
                        case "equals":
                            return cellValue === filterValue;
                        case "startsWith":
                            return cellValue.startsWith(filterValue);
                        case "endsWith":
                            return cellValue.endsWith(filterValue);
                        case "greaterThan":
                            return !isNaN(cellValue) && !isNaN(filterValue) && 
                                   Number(cellValue) > Number(filterValue);
                        case "lessThan":
                            return !isNaN(cellValue) && !isNaN(filterValue) && 
                                   Number(cellValue) < Number(filterValue);
                        default:
                            return true;
                    }
                });
            });
            
            setFilteredTableData(filtered);
        }
        
        closeFilterModal();
    };
    
    // Clear all filters
    const clearFilters = () => {
        setFilterConditions([]);
        setFilteredTableData(tableData);
        closeFilterModal();
    };
    
    return {
        filterModalOpen,
        filterConditions,
        availableColumns,
        openFilterModal,
        closeFilterModal,
        addFilterCondition,
        removeFilterCondition,
        updateFilterCondition,
        applyFilters,
        clearFilters
    };
};